import React, { useMemo } from 'react';

import parse from 'html-react-parser';

import { escapeRegex } from '../../lib/stringUtils';

import { useEntityReplacer } from '../../hooks/useEntityReplacer';

// <mark> keeps the inherited text color; the fill reads on the light bubble surfaces.
const HIGHLIGHT_MARK_CLASS = 'rounded-[3px] bg-yellow-200 text-inherit dark:bg-yellow-400/50';

// This is only to display a richdown variant, and all it shows is the core content of the message, styled correctly.
// Richdown base wrapping and entity replacing/expansion
const RichdownContent = ({
  html,
  richdownVariant,
  entitySnapshots = {},
  containsOnlyEntities = false,
  containsOnlyEmojiEntities = false,
  popoverProfileEntities = false,
  truncated = false,
  // When set (e.g. search results), occurrences in plain text nodes render inside a <mark>.
  highlightTerm = null,
}) => {
  const { replaceEntityNode } = useEntityReplacer(entitySnapshots, {
    containsOnlyEntities,
    containsOnlyEmojiEntities,
    popoverProfileEntities,
    truncated,
  });
  const options = useMemo(() => {
    const trimmedTerm = typeof highlightTerm === 'string' ? highlightTerm.trim() : '';
    if (!trimmedTerm) return { replace: replaceEntityNode };

    // Capturing group so split() interleaves matches at the odd indexes.
    const highlightRegExp = new RegExp(`(${escapeRegex(trimmedTerm)})`, 'gi');
    return {
      replace: (domNode) => {
        const entityReplacement = replaceEntityNode(domNode);
        if (entityReplacement) return entityReplacement;

        if (domNode.type !== 'text' || !domNode.data) return undefined;
        const parts = domNode.data.split(highlightRegExp);
        if (parts.length === 1) return undefined;

        return (
          <>
            {parts.map((part, index) =>
              index % 2 === 1 ? (
                <mark key={index} className={HIGHLIGHT_MARK_CLASS}>
                  {part}
                </mark>
              ) : (
                part
              ),
            )}
          </>
        );
      },
    };
  }, [highlightTerm, replaceEntityNode]);
  const content = parse(html || '', options);

  if (richdownVariant === 'inline') {
    return <span>{content}</span>;
  }

  return <div className={`richdown-content--base ${richdownVariant || ''}`}>{content}</div>;
};

export default RichdownContent;
